// apps/web/src/components/astral/AstralNodeItem.tsx
import { useState, useRef, useEffect } from "react";
import type { CSSProperties, MouseEvent as RMouseEvent, KeyboardEvent as RKeyboardEvent } from "react";
import { Plus, Minus, ExternalLink } from "lucide-react";
import type { AstralNode, AstralThemeDefinition, AstralNodeShape } from "../../types/astral";

interface AstralNodeItemProps {
  node: AstralNode;
  isRoot: boolean;
  isSelected: boolean;
  isEditing: boolean;
  hasChildren: boolean;
  childCount: number;
  theme: AstralThemeDefinition;
  defaultShape?: AstralNodeShape;
  position: { x: number; y: number; width: number; height: number };
  isDragging?: boolean;
  isDropTarget?: boolean;
  readOnly?: boolean;
  onSelect: (nodeId: string) => void;
  onStartEdit: (nodeId: string) => void;
  onSaveText: (nodeId: string, newText: string) => void;
  onCancelEdit: () => void;
  onAddChild: (parentId: string) => void;
  onToggleCollapse: (nodeId: string) => void;
  onStartDrag?: (nodeId: string, clientX: number, clientY: number) => void;
}

export function AstralNodeItem({
  node,
  isRoot,
  isSelected,
  isEditing,
  hasChildren,
  childCount,
  theme,
  defaultShape = "rounded",
  position,
  isDragging = false,
  isDropTarget = false,
  readOnly = false,
  onSelect,
  onStartEdit,
  onSaveText,
  onCancelEdit,
  onAddChild,
  onToggleCollapse,
  onStartDrag,
}: AstralNodeItemProps) {
  const [editText, setEditText] = useState(node.text);
  const [isHovered, setIsHovered] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const pointerDownPosRef = useRef<{ x: number; y: number } | null>(null);
  const isPotentialDragRef = useRef(false);

  useEffect(() => {
    setEditText(node.text);
  }, [node.text]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleMouseDown = (e: RMouseEvent) => {
    if (e.button !== 0 || isEditing || readOnly) return;
    const target = e.target as HTMLElement;
    if (target.closest("button") || target.closest("input") || target.closest("a")) return;

    pointerDownPosRef.current = { x: e.clientX, y: e.clientY };
    isPotentialDragRef.current = true;
  };

  const handleMouseMove = (e: RMouseEvent) => {
    if (!isPotentialDragRef.current || !pointerDownPosRef.current || isEditing) return;
    const dx = e.clientX - pointerDownPosRef.current.x;
    const dy = e.clientY - pointerDownPosRef.current.y;
    if (Math.hypot(dx, dy) >= 4) {
      isPotentialDragRef.current = false;
      pointerDownPosRef.current = null;
      onStartDrag?.(node.id, e.clientX, e.clientY);
    }
  };

  const handleMouseUp = () => {
    isPotentialDragRef.current = false;
    pointerDownPosRef.current = null;
  };

  const handleKeyDown = (e: RKeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      onSaveText(node.id, editText.trim() || node.text);
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      setEditText(node.text);
      onCancelEdit();
    }
  };

  const handleBlur = () => {
    if (isEditing) {
      onSaveText(node.id, editText.trim() || node.text);
    }
  };

  // Cores dinâmicas de acordo com o tema e nível
  const bgColor = isRoot
    ? theme.nodeRootBg
    : node.color || theme.nodeGeneralBg;
  const textColor = isRoot
    ? theme.nodeRootText
    : theme.nodeGeneralText;
  const borderColor = isRoot
    ? theme.nodeRootBorder
    : isSelected
    ? "var(--apple-blue)"
    : node.color
    ? node.color
    : theme.nodeGeneralBorder;

  // Formato do nó (MindMeister Shapes)
  const activeShape: AstralNodeShape = node.shape || defaultShape || "rounded";

  let shapeStyles: CSSProperties = {};
  if (activeShape === "rectangle") {
    shapeStyles = {
      borderRadius: "3px",
      backgroundColor: bgColor,
      border: `1.5px solid ${borderColor}`,
      padding: isRoot ? "10px 18px" : "7px 14px",
    };
  } else if (activeShape === "underline") {
    shapeStyles = {
      borderRadius: "0px",
      backgroundColor: "transparent",
      border: "none",
      borderBottom: `2.5px solid ${borderColor}`,
      padding: "5px 10px",
    };
  } else if (activeShape === "bubble") {
    shapeStyles = {
      borderRadius: "9999px",
      backgroundColor: bgColor,
      border: `1.5px solid ${borderColor}`,
      padding: isRoot ? "10px 24px" : "7px 20px",
    };
  } else if (activeShape === "card") {
    shapeStyles = {
      borderRadius: "8px",
      backgroundColor: "rgba(17, 22, 34, 0.95)",
      border: "1px solid rgba(255, 255, 255, 0.1)",
      borderLeft: `4px solid ${borderColor}`,
      padding: isRoot ? "10px 18px" : "8px 14px",
      boxShadow: "0 4px 16px rgba(0, 0, 0, 0.35)",
    };
  } else {
    // rounded padrão
    shapeStyles = {
      borderRadius: isRoot ? "14px" : "10px",
      backgroundColor: bgColor,
      border: `1.5px solid ${borderColor}`,
      padding: isRoot ? "10px 20px" : "8px 14px",
    };
  }

  return (
    <div
      data-astral-node="true"
      style={{
        position: "absolute",
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${position.width}px`,
        minHeight: `${position.height}px`,
        zIndex: isDragging ? 100 : isDropTarget ? 50 : isSelected ? 30 : 10,
        opacity: isDragging ? 0.85 : 1,
        userSelect: "none",
        cursor: isDragging ? "grabbing" : "grab",
        transition: isDragging ? "none" : "transform 0.12s ease, box-shadow 0.15s ease",
        transform: isDragging ? "scale(1.04)" : isSelected ? "scale(1.02)" : "scale(1)",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        handleMouseUp();
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(node.id);
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (!readOnly) onStartEdit(node.id);
      }}
    >
      {/* Indicador visual de Drop Target quando arrastando outro nó sobre este */}
      {isDropTarget && (
        <div
          style={{
            position: "absolute",
            top: "-26px",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#6366f1",
            color: "#ffffff",
            fontSize: "10px",
            fontWeight: 700,
            padding: "3px 9px",
            borderRadius: "12px",
            whiteSpace: "nowrap",
            boxShadow: "0 4px 14px rgba(99, 102, 241, 0.6)",
            pointerEvents: "none",
            zIndex: 70,
            display: "flex",
            alignItems: "center",
            gap: "4px",
            letterSpacing: "0.02em",
          }}
        >
          <span>↳ Solte para conectar como filho</span>
        </div>
      )}

      {/* Caixa do Card do Tópico */}
      <div
        style={{
          width: "100%",
          color: textColor,
          boxShadow: isDropTarget
            ? "0 0 0 3px #6366f1, 0 0 24px rgba(99, 102, 241, 0.65)"
            : isDragging
            ? "0 18px 40px rgba(0, 0, 0, 0.7), 0 0 0 2px #6366f1"
            : isSelected
            ? "0 0 0 2px rgba(56, 139, 253, 0.45), 0 8px 24px rgba(0, 0, 0, 0.5)"
            : activeShape === "card"
            ? "0 4px 14px rgba(0, 0, 0, 0.3)"
            : "0 2px 8px rgba(0, 0, 0, 0.25)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          transition: isDragging ? "none" : "border-color 0.15s ease, box-shadow 0.15s ease",
          boxSizing: "border-box",
          ...shapeStyles,
        }}
      >
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: isRoot && node.badge ? "column" : "row",
            alignItems: isRoot && node.badge ? "flex-start" : "center",
            gap: isRoot && node.badge ? "5px" : "6px",
          }}
        >
          {/* Badge Numérica ou Funil (estilo MindMeister) */}
          {node.badge && (
            <span
              style={{
                fontSize: "10px",
                fontWeight: 700,
                padding: "2px 6px",
                borderRadius: "4px",
                backgroundColor: isRoot ? "rgba(255, 255, 255, 0.25)" : "rgba(239, 68, 68, 0.2)",
                color: isRoot ? "#ffffff" : "#f87171",
                border: isRoot ? "1px solid rgba(255, 255, 255, 0.3)" : "1px solid rgba(239, 68, 68, 0.3)",
                flexShrink: 0,
              }}
            >
              {node.badge}
            </span>
          )}

          {/* Texto do Tópico ou Input de Edição */}
          {isEditing ? (
            <input
              ref={inputRef}
              type="text"
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              style={{
                width: "100%",
                background: "rgba(0, 0, 0, 0.5)",
                border: "1px solid var(--apple-blue)",
                borderRadius: "4px",
                color: "#ffffff",
                fontSize: isRoot ? "14px" : "12.5px",
                fontWeight: isRoot ? 700 : 500,
                padding: "2px 6px",
                outline: "none",
              }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span
              style={{
                fontSize: isRoot ? "14.5px" : "12.5px",
                fontWeight: isRoot ? 700 : 500,
                color: activeShape === "underline" && !isRoot ? (theme.id === "meister" ? "#1f2937" : "#f1f5f9") : textColor,
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.35,
                letterSpacing: isRoot ? "0.02em" : "normal",
              }}
            >
              {node.text}
            </span>
          )}
        </div>

        {/* Link Externo (se houver) */}
        {node.link && (
          <a
            href={node.link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title={`Abrir link: ${node.link}`}
            style={{
              color: isRoot ? "#ffffff" : "var(--apple-blue)",
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              opacity: 0.85,
            }}
          >
            <ExternalLink size={12} />
          </a>
        )}
      </div>

      {/* Botão de Adição Rápida (+) na lateral do nó (Círculo Perfeito e Polido) */}
      {(isSelected || isHovered) && !readOnly && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onAddChild(node.id);
          }}
          title="Adicionar tópico filho (ou aperte Tab)"
          style={{
            position: "absolute",
            right: "-12px",
            top: "50%",
            transform: "translateY(-50%)",
            width: "22px",
            height: "22px",
            minWidth: "22px",
            borderRadius: "50%",
            padding: 0,
            backgroundColor: "var(--apple-blue)",
            color: "#ffffff",
            border: "2px solid #0b0e14",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.4)",
            zIndex: 40,
            transition: "transform 0.15s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-50%) scale(1.18)")}
          onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(-50%) scale(1)")}
        >
          <Plus size={13} strokeWidth={2.5} />
        </button>
      )}

      {/* Botão de Colapso / Expansão na ramificação (Círculo Perfeito) */}
      {hasChildren && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleCollapse(node.id);
          }}
          title={node.isCollapsed ? `Expandir ${childCount} tópicos` : "Recolher ramificação"}
          style={{
            position: "absolute",
            right: "-26px",
            top: "50%",
            transform: "translateY(-50%)",
            width: "18px",
            height: "18px",
            minWidth: "18px",
            borderRadius: "50%",
            padding: 0,
            backgroundColor: node.isCollapsed ? "var(--apple-blue)" : "rgba(255, 255, 255, 0.15)",
            color: node.isCollapsed ? "#ffffff" : "var(--ink-soft)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            fontSize: "9px",
            fontWeight: 700,
            zIndex: 35,
          }}
        >
          {node.isCollapsed ? childCount : <Minus size={10} />}
        </button>
      )}

      {/* Dica Flutuante de Atalhos MindMeister (Aparece quando Selecionado) */}
      {isSelected && !isEditing && !readOnly && (
        <div
          style={{
            position: "absolute",
            bottom: "-38px",
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(11, 14, 20, 0.95)",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            borderRadius: "6px",
            padding: "3px 8px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "10px",
            color: "var(--ink-muted)",
            whiteSpace: "nowrap",
            pointerEvents: "none",
            boxShadow: "0 4px 14px rgba(0, 0, 0, 0.4)",
            zIndex: 45,
          }}
        >
          <span>
            <strong style={{ color: "var(--apple-blue)", padding: "1px 4px", background: "rgba(56, 139, 253, 0.15)", borderRadius: "3px" }}>Tab</strong> filho
          </span>
          <span>•</span>
          <span>
            <strong style={{ color: "var(--apple-green)", padding: "1px 4px", background: "rgba(46, 160, 67, 0.15)", borderRadius: "3px" }}>Enter</strong> irmão
          </span>
        </div>
      )}
    </div>
  );
}
